// Atoms
import EIcon from 'components/atoms/media/Icon.vue';
import EImage from 'components/atoms/media/image/index.vue';
import ELogo from 'components/atoms/media/Logo.vue';
import EVector from 'components/atoms/media/Vector.vue';
import ECopyright from 'components/atoms/typography/Copyright.vue';
import EKbd from 'components/atoms/Kbd.vue';
import ELinkBase from 'components/atoms/interactive-elements/LinkBase.vue';
import EText from 'components/atoms/typography/Text.vue';

// Molecules
import EButton from 'components/molecules/Button.vue';
import EAppearance from 'components/molecules/Appearance.vue';
import EHeading from 'components/molecules/Heading.vue';
import EAlert from 'components/molecules/Alert.vue';
import EField from 'components/molecules/Field.vue';
import EInput from 'components/molecules/Input.vue';
import ECheckbox from 'components/molecules/Checkbox.vue';
import ERadio from 'components/molecules/Radio.vue';
import EInputPin from 'components/molecules/InputPin.vue';
import ESelect from 'components/molecules/Select.vue';
import EInputNumber from 'components/molecules/InputNumber.vue';
import EMenuItem from 'components/Menu/MenuItem.vue';

import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionContent,
  AccordionTrigger,
  // basicHtml,
  accordionDefinition,
  accordionPropsDocs,
  accordionItemPropsDocs
} from 'components/Accordion';

import EQrCode from 'components/QrCode/QrCode.vue';
import EEditor from 'components/Editor/Editor.vue';

// Organisms
import EFormLayout from 'components/organisms/FormLayout.vue';
import ELayout from 'components/Layout/Layout.vue';
import EToast from 'components/organisms/Toast.vue';
import ECard from 'components/organisms/Card.vue';
import EMenu from 'components/Menu/Menu.vue';
import EDebug from 'components/Debug/Debug.vue';
import EGridLayout from 'components/organisms/GridLayout.vue';

// Composables
import { useToast } from 'composables/useToast';

import { useStateStore } from 'composables/useStateStore';

// Styles
import 'tailwindcss/index.css';
import '../assets/css/main.css';
import '../assets/css/elements.css';

import { defineEConnectElement } from './defineEConnectElementsApi';
import { defaultElementOptions } from './eConnectElementOptions';
import { createPinia } from 'pinia';

export type { ElementDefinition, ElementDoc, PropDoc, StoryDoc, StyleDoc } from 'shared';

const pinia = createPinia();

// Atoms
const eIconComponent = defineEConnectElement(EIcon, defaultElementOptions);
const eImageComponent = defineEConnectElement(EImage, defaultElementOptions);
const eLogoComponent = defineEConnectElement(ELogo, defaultElementOptions);
const eVectorComponent = defineEConnectElement(EVector, defaultElementOptions);
const eCopyrightComponent = defineEConnectElement(ECopyright, defaultElementOptions);
const eKbdComponent = defineEConnectElement(EKbd, defaultElementOptions);
const eLinkBaseComponent = defineEConnectElement(ELinkBase, defaultElementOptions);
const eTextComponent = defineEConnectElement(EText, defaultElementOptions);

const eButtonComponent = defineEConnectElement(EButton, {
  ...defaultElementOptions,
  formSubmitter: true
});

// Molecules
// onst eButtonComponent = EButtonCE;
const eAppearanceComponent = defineEConnectElement(EAppearance, defaultElementOptions);
const eHeadingComponent = defineEConnectElement(EHeading, defaultElementOptions);
const eFieldComponent = defineEConnectElement(EField, defaultElementOptions);
const eAlertComponent = defineEConnectElement(EAlert, defaultElementOptions);
const eToastComponent = defineEConnectElement(EToast, defaultElementOptions);

const eInputComponent = defineEConnectElement(EInput, {
  ...defaultElementOptions,
  formAssociated: true
});
const eCheckboxComponent = defineEConnectElement(ECheckbox, {
  ...defaultElementOptions,
  formAssociated: true
});
const eRadioComponent = defineEConnectElement(ERadio, {
  ...defaultElementOptions,
  formAssociated: true
});
const eInputPinComponent = defineEConnectElement(EInputPin, {
  ...defaultElementOptions,
  formAssociated: true
});
const eSelectComponent = defineEConnectElement(ESelect, {
  ...defaultElementOptions,
  formAssociated: true
});
const eInputNumberComponent = defineEConnectElement(EInputNumber, {
  ...defaultElementOptions,
  formAssociated: true
});
const eMenuItemComponent = defineEConnectElement(EMenuItem, defaultElementOptions);

// Organisms
const eFormLayoutComponent = defineEConnectElement(EFormLayout, defaultElementOptions);
const eLayoutComponent = defineEConnectElement(ELayout, defaultElementOptions);
const eCardComponent = defineEConnectElement(ECard, defaultElementOptions);
const eMenuComponent = defineEConnectElement(EMenu, defaultElementOptions);
const eDebugComponent = defineEConnectElement(EDebug, defaultElementOptions);
const eGridLayoutComponent = defineEConnectElement(EGridLayout, defaultElementOptions);

// Accordion Items
const accordionComponent = defineEConnectElement(Accordion, defaultElementOptions);
const accordionItemComponent = defineEConnectElement(AccordionItem, defaultElementOptions);
const accordionHeaderComponent = defineEConnectElement(AccordionHeader, defaultElementOptions);
const accordionContentComponent = defineEConnectElement(AccordionContent, defaultElementOptions);
const accordionTriggerComponent = defineEConnectElement(AccordionTrigger, defaultElementOptions);

//QrCode
const eQrCodeComponent = defineEConnectElement(EQrCode, defaultElementOptions);

const eEditorComponent = defineEConnectElement(EEditor, {
  ...defaultElementOptions,
  customElementOptions: {
    shadowRoot: true
  }
});
export {
  // Atoms
  eIconComponent,
  eImageComponent,
  eLogoComponent,
  eVectorComponent,
  eCopyrightComponent,
  eKbdComponent,
  eLinkBaseComponent,
  eTextComponent,

  // Molecules
  eButtonComponent,
  eAppearanceComponent,
  eHeadingComponent,
  eAlertComponent,
  eFieldComponent,
  eToastComponent,
  eInputComponent,
  eCheckboxComponent,
  eRadioComponent,
  eInputPinComponent,
  eSelectComponent,
  eInputNumberComponent,
  eMenuItemComponent,

  // Organisms
  eFormLayoutComponent,
  eLayoutComponent,
  eCardComponent,
  eMenuComponent,
  eDebugComponent,
  eGridLayoutComponent,

  // Accordion Elements
  accordionComponent,
  accordionItemComponent,
  accordionHeaderComponent,
  accordionContentComponent,
  accordionDefinition,
  accordionPropsDocs,
  accordionItemPropsDocs,
  // basicHtml,

  //QrCode
  eQrCodeComponent,
  eEditorComponent,

  // Composables
  useToast,
  useStateStore,
  defineEConnectElement,
  defaultElementOptions,
  pinia
};

const define = (tag: string, ctor: CustomElementConstructor) => {
  if (!customElements.get(tag)) {
    customElements.define(tag, ctor);
  }
};

export function register(): void {
  define('e-icon', eIconComponent);
  define('e-image', eImageComponent);
  define('e-logo', eLogoComponent);
  define('e-vector', eVectorComponent);
  define('e-copyright', eCopyrightComponent);
  define('e-kbd', eKbdComponent);
  define('e-link-base', eLinkBaseComponent);
  define('e-text', eTextComponent);

  define('e-input', eInputComponent);
  define('e-checkbox', eCheckboxComponent);
  define('e-radio', eRadioComponent);
  define('e-input-pin', eInputPinComponent);
  define('e-select', eSelectComponent);
  define('e-input-number', eInputNumberComponent);
  define('e-button', eButtonComponent);
  define('e-appearance', eAppearanceComponent);
  define('e-heading', eHeadingComponent);
  define('e-alert', eAlertComponent);
  define('e-field', eFieldComponent);
  define('e-toast', eToastComponent);
  define('e-menu-item', eMenuItemComponent);

  define('e-accordion', accordionComponent);
  define('e-accordion-item', accordionItemComponent);
  define('e-accordion-header', accordionHeaderComponent);
  define('e-accordion-content', accordionContentComponent);
  define('e-accordion-trigger', accordionTriggerComponent);

  define('e-qr-code', eQrCodeComponent);

  define('e-form-layout', eFormLayoutComponent);
  define('e-layout', eLayoutComponent);
  define('e-card', eCardComponent);
  define('e-menu', eMenuComponent);
  define('e-debug', eDebugComponent);
  define('e-grid-layout', eGridLayoutComponent);

  define('e-editor', eEditorComponent);
}

// register global typings
declare module 'vue' {
  export interface GlobalComponents {
    // Atoms
    EIcon: typeof eIconComponent;
    EImage: typeof eImageComponent;
    ELogo: typeof eLogoComponent;
    EVector: typeof eVectorComponent;
    ECopyright: typeof eCopyrightComponent;
    EKbd: typeof eKbdComponent;
    ELinkBase: typeof eLinkBaseComponent;
    EText: typeof eTextComponent;

    // Molecules
    EInput: typeof eInputComponent;
    ECheckbox: typeof eCheckboxComponent;
    ERadio: typeof eRadioComponent;
    EInputPin: typeof eInputPinComponent;
    ESelect: typeof eSelectComponent;
    EInputNumber: typeof eInputNumberComponent;
    EButton: typeof eButtonComponent;
    EAppearance: typeof eAppearanceComponent;
    EHeading: typeof eHeadingComponent;
    EAlert: typeof eAlertComponent;
    EField: typeof eFieldComponent;
    EToast: typeof eToastComponent;
    EMenuItem: typeof eMenuItemComponent;

    // Organisms
    EFormLayout: typeof eFormLayoutComponent;
    ELayout: typeof eLayoutComponent;
    ECard: typeof eCardComponent;
    EMenu: typeof eMenuComponent;
    EDebug: typeof eDebugComponent;
    EGridLayout: typeof eGridLayoutComponent;

    EAccordion: typeof accordionComponent;
    EAccordionItem: typeof accordionItemComponent;
    EAccordionHeader: typeof accordionHeaderComponent;
    EAccordionContent: typeof accordionContentComponent;
    EAccordionTrigger: typeof accordionTriggerComponent;

    EQrCode: typeof eQrCodeComponent;

    EEditor: typeof eEditorComponent;
  }
}
